import './style.scss'
import { ACCOUNT_TYPE, selectUserData, updateValue } from '../accountSlice'
import { companyInvoiceRecipients, invoiceRecipients } from 'rest/urls'
import { parseApiErrors, pickFieldError } from 'rest/apiErrors'
import { useDispatch, useSelector } from 'react-redux'
import useTranslation from 'hooks/useTranslation'
import Checkbox from 'components/inputs/Checkbox'
import { useHistory } from 'react-router-dom'
import Submit from 'components/inputs/Submit'
import Input from 'components/inputs/Input'
import Popup from 'components/popups/Popup'
import { STATUS } from 'utils/constants'
import { useRef, useState } from 'react'
import { PATCH } from 'rest/request'

const type = ACCOUNT_TYPE.invoiceRecipients
const EMAIL_REGEX = /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]+$/
const DEFAULT_MAX_EMAILS = 10

const EditInvoiceRecipientsPopup = ({ companyId }) => {
  const { data } = useSelector(state => selectUserData(state, type))
  const maxEmails = data?.maxEmails || DEFAULT_MAX_EMAILS
  const settings = companyId
    ? data?.companies?.find(({ id }) => id === companyId)
    : data?.personal

  // invoiceToRepresentatives świadomie pomijamy — pole nie wysłane = backend zostawia bez zmian
  const [values, setValues] = useState(() => companyId
    ? { invoiceToOwner: !!settings?.invoiceToOwner }
    : { invoiceToOwnEmail: !!settings?.invoiceToOwnEmail })
  const [emails, setEmails] = useState(() => Array.isArray(settings?.invoiceEmails) ? settings.invoiceEmails : [])
  // odznaczone adresy — zostają na liście do zapisu, „Zapisz” je usuwa
  const [removed, setRemoved] = useState([])
  const [newEmail, setNewEmail] = useState('')
  const [warning, setWarning] = useState('')
  const [status, setStatus] = useState(STATUS.idle)
  const inputRef = useRef(null)

  const dispatch = useDispatch()
  const { goBack } = useHistory()
  const { t } = useTranslation('account.invoiceRecipients')
  const isPending = status === STATUS.pending

  // pole tekstowe może zawierać kilka adresów (np. wklejonych po przecinku)
  const parseInput = (value = newEmail) => value.split(/[\s,;]+/).map(e => e.trim().toLowerCase()).filter(Boolean)

  const addEmails = () => {
    const parsed = parseInput()
    if (!parsed.length) return emails.filter(e => !removed.includes(e))

    const invalid = parsed.filter(e => !EMAIL_REGEX.test(e))
    if (invalid.length) {
      setWarning(`${t('wrongEmail')}: ${invalid.join(', ')}`)
      return null
    }

    const next = [...emails, ...parsed.filter(e => !emails.includes(e))]
      .filter((e, i, arr) => arr.indexOf(e) === i)
    // ponowne wpisanie odznaczonego adresu przywraca go
    const nextRemoved = removed.filter(e => !parsed.includes(e))
    if (next.filter(e => !nextRemoved.includes(e)).length > maxEmails) {
      setWarning(t('tooManyEmails').replace('{max}', maxEmails))
      return null
    }

    setEmails(next)
    setRemoved(nextRemoved)
    setNewEmail('')
    setWarning('')
    return next.filter(e => !nextRemoved.includes(e))
  }

  const toggleEmail = (email, keep) => {
    setRemoved(prev => keep ? prev.filter(e => e !== email) : [...prev, email])
    setWarning('')
  }

  const activeCount = emails.filter(e => !removed.includes(e)).length

  const submit = async () => {
    // niedodany adres z pola tekstowego też zapisujemy
    const invoiceEmails = addEmails()
    if (!invoiceEmails || !settings) return

    setStatus(STATUS.pending)
    try {
      const url = companyId ? companyInvoiceRecipients(companyId) : invoiceRecipients
      const response = await PATCH(url, { body: { ...values, invoiceEmails } }, false)
      if (!response?.data) throw new Error('Wrong response')

      // odpowiedź ma ten sam kształt co GET — podmieniamy cały stan
      dispatch(updateValue({ type, name: 'data', value: response.data }))
      goBack()

    } catch (error) {
      setStatus(STATUS.failed)
      const { fields, message } = parseApiErrors(error)
      setWarning(pickFieldError(fields, ['invoiceEmails']) || message || t('saveError'))
    }
  }

  return (
    <Popup>
      <div className='popup__verification invoiceRecipients'>
        <div className='popup__title'>
          <div>{t('title')}</div>
          <div>{companyId ? settings?.name : t('personalSubtitle')}</div>
        </div>

        {!settings
          ? <div className='popup__form'><p className='verification__warning'>{t('saveError')}</p></div>
          : <div className='popup__form invoiceRecipients__form'>
            <div className='invoiceRecipients__section'>
              <div className='invoiceRecipients__heading'>
                <span>{t('whoToSend')}</span>
                <span className='invoiceRecipients__counter'>{activeCount}/{maxEmails}</span>
              </div>

              <div className='invoiceRecipients__card --list'>
                <div className='invoiceRecipients__item'>
                  {companyId
                    ? <Checkbox
                      name='invoiceToOwner'
                      label={t('toOwner')}
                      disabled={isPending}
                      checked={values.invoiceToOwner}
                      changeHandler={e => setValues(prev => ({ ...prev, invoiceToOwner: e.target.checked }))} />
                    : <Checkbox
                      name='invoiceToOwnEmail'
                      label={t('toOwnEmail')}
                      disabled={isPending}
                      checked={values.invoiceToOwnEmail}
                      changeHandler={e => setValues(prev => ({ ...prev, invoiceToOwnEmail: e.target.checked }))} />}
                </div>

                {emails.map(email => {
                  const isRemoved = removed.includes(email)
                  return <div key={email} className={`invoiceRecipients__item${isRemoved ? ' --removed' : ''}`}>
                    <Checkbox
                      name={`invoiceEmail-${email}`}
                      label={<>
                        <span className='invoiceRecipients__email'>{email}</span>
                        {isRemoved && <span className='invoiceRecipients__removedHint'>{t('willBeRemoved')}</span>}
                      </>}
                      disabled={isPending}
                      checked={!isRemoved}
                      changeHandler={e => toggleEmail(email, e.target.checked)} />
                  </div>
                })}
              </div>

              {activeCount < maxEmails
                ? <Input
                  ref={inputRef}
                  className='invoiceRecipients__input'
                  name='invoiceEmail'
                  type='email'
                  inputMode='email'
                  autoComplete='off'
                  value={newEmail}
                  label={t('addEmail')}
                  maxLength='255'
                  warning={warning}
                  placeholder={t('enterEmail')}
                  disabled={isPending}
                  icon={<button
                    type='button'
                    className='invoiceRecipients__add'
                    disabled={isPending || !newEmail.trim()}
                    onClick={() => addEmails()}>
                    {t('add')}
                  </button>}
                  iconPosRight
                  enterClicked={() => addEmails()}
                  changeHandler={e => {
                    setNewEmail(e.target.value)
                    if (warning) setWarning('')
                  }} />
                : <p className='invoiceRecipients__hint'>{warning || t('limitReached')}</p>}
            </div>

            <Submit
              text={t('save')}
              block={isPending}
              isLoading={isPending}
              action={submit} />
          </div>}
      </div>
    </Popup>
  )
}

export default EditInvoiceRecipientsPopup
